import Vue from 'vue';
import Router from 'vue-router';

import { getPublicProfiles } from '@homeservices/api-kit/search/publicprofile';
import ProDeskReporting from '@/views/ProDeskReporting';
import ProPublicProfile from '@/views/ProPublicProfile';
import CredentialsModal from '@/views/ProPublicProfile/views/CredentialsModal';
import ShareModal from '@/views/ProPublicProfile/views/ShareModal';
import Account from '@/views/Account';
import FAQ from '@/views/FAQ';
import Info from '@/views/Account/Pro/views/Info';
import Points from '@/views/Account/Pro/views/Points';
import Reviews from '@/views/Account/Pro/views/Reviews';
import Occupation from '@/views/Account/Pro/views/Occupation';
import ConsumerDashboard from '@/views/Consumer/views/Dashboard';
import ConsumerTriage from '@/views/Consumer/views/Dashboard/views/Triage';
import ConsumerMessages from '@/views/Consumer/views/Messages';
import ConsumerInbox from '@/views/Consumer/views/Inbox';
import About from '@/views/About';
import AboutUs from '@/views/About/views/AboutUs';
import FrequentQuestions from '@/views/About/views/FrequentQuestions';
import ContactUs from '@/views/About/views/ContactUs';
import ServiceTerms from '@/views/About/views/LegalInfo/views/ServiceTerms';
import PrivacyPolicy from '@/views/About/views/LegalInfo/views/PrivacyPolicy';
import JobRequest from '@/views/JobRequest';
import JobRequestConfirmation from '@/views/JobRequest/Confirmation/views/NextSteps';
import TosConfirmation from '@/views/JobRequest/Confirmation/views/Tos';
import JobRequestDetails from '@/views/JobRequest/Details';
import JobRequestMatches from '@/views/JobRequest/Matches';
import JobRequestOverview from '@/views/JobRequest/Overview';
import Redirect from '@/views/Redirect';
import Logout from '@/views/Logout';
import Homepage from '@/views/Homepage';
import ProHomepage from '@/views/ProHomepage';
import ErrorPage from '@/views/Error';
import GenericError from '@/views/Error/components/GenericError';
import PermissionException from '@/views/PermissionException';
import Login from '@/views/Login';
import LoginAs from '@/views/LoginAs';
import PasswordForgot from '@/views/PasswordForgot';
import PasswordReset from '@/views/PasswordReset';
import AuthRedirect from '@/views/AuthRedirect';
import AuthWithToken from '@/views/AuthWithToken';
import OneTimeCode from '@/views/OneTimeCode';
import OneTimeCodeSignIn from '@/views/OneTimeCodeSignIn';
import CreateAPassword from '@/views/CreateAPassword';
import WelcomePassword from '@/views/WelcomePassword';
import TosPassword from '@/views/TosPassword';

import NewConsumerReviews from '@/views/Consumer/views/NewReviews';
import NewReview from '@/views/Consumer/views/NewReviews/views/NewReview';

import TextOptIn from '@/views/Consumer/views/TextOptIn';
import OptInSuccess from '@/views/Consumer/views/TextOptIn/views/OptInSuccess';
import OptInError from '@/views/Consumer/views/TextOptIn/views/OptInError';
import NewDashboard from '@/views/Dashboard';
import DashboardSidebar from '@/views/Dashboard/views/jobs/Sidebar';
import DashboardContent from '@/views/Dashboard/views/jobs/Content';
import Performance from '@/views/Dashboard/views/Performance';
import Messages from '@/views/Dashboard/views/Messages';
import Agenda from '@/views/Dashboard/views/Agenda';
import Notifications from '@/views/Dashboard/views/Notifications';
import ToolsAndOffers from '@/views/Account/Pro/views/ToolsAndOffers';
import InviteAPro from '@/views/Account/Pro/views/InviteAPro';
import Education from '@/views/Dashboard/views/Education';
import Video from '@/views/Dashboard/views/VideoQuiz/Video';
import EducationQuiz from '@/views/Dashboard/views/VideoQuiz/EducationQuiz';

import { createCheckUserStatus } from './create-check-user-status';
import { createPageLoadEvent } from './page-load-event';

import { emailRedirects } from './email-redirects';

Vue.use(Router);

export function createRouter (store, events) {
  const routes = [
    ...emailRedirects,

    {
      name: 'pro-desk-reporting',
      path: '/hdstore/:storeId',
      redirect: { name: 'pro-desk-reporting::report' },
      component: ProDeskReporting,
      meta: {
        layout: 'ProDeskReportingLayout',
      },
      children: [
        {
          name: 'pro-desk-reporting::testimonials',
          path: 'testimonials',
          component: ProDeskReporting,
          meta: {
            showTestimonials: true,
            showResources: false,
            showRequirements: false,
          },
        },
        {
          name: 'pro-desk-reporting::report',
          path: 'report',
          component: ProDeskReporting,
          meta: {
            showTestimonials: false,
            showResources: false,
            showRequirements: false,
          },
        },
        {
          name: 'pro-desk-reporting::resources',
          path: 'resources',
          component: ProDeskReporting,
          meta: {
            showTestimonials: false,
            showResources: true,
            showRequirements: false,
          },
        },
        {
          name: 'pro-desk-reporting::requirements',
          path: 'requirements',
          component: ProDeskReporting,
          meta: {
            showTestimonials: false,
            showResources: false,
            showRequirements: true,
          },
        },

      ],
    },

    {
      name: 'faq',
      path: '/faq',
      component: FAQ,
      meta: {
        layout: 'AppLayout',
      },
    },

    // redirect old style slugs to new public profile pages
    {
      path: '/s/:slug',
      async beforeEnter (to, from, next) {
        const { data } = await getPublicProfiles({
          slugs: to.params.slug,
        });

        if (data.results.length) {
          next({
            name: 'pro-public-profile',
            params: {
              serviceProviderOccupationId: data.results[0].id,
            },
          });
        } else {
          next({ name: 'not-found' });
        }
      },
    },

    {
      name: 'pro-public-profile',
      path: '/public-profile/:serviceProviderOccupationId',
      component: ProPublicProfile,
      meta: {
        layout: 'AppLayout',
      },
      children: [
        {
          name: 'pro-public-profile::reviews',
          path: 'reviews',
          meta: {
            reviews: true,
          },
          props: true,
        },
        {
          name: 'pro-public-profile::credentials',
          path: 'credentials',
          component: CredentialsModal,
          props: true,
        },
        {
          name: 'pro-public-profile::share',
          path: 'share',
          component: ShareModal,
        },
      ],
    },
    {
      path: '/account/consumer',
      redirect: { name: 'account::info' },
    },
    {
      path: '/account',
      component: Account,
      meta: {
        requiresAuth: true,
        layout: 'AppLayout', // TODO replace with AccountLayout
      },
      children: [
        {
          name: 'account::info',
          path: '',
          component: Info,
          // Don't want this since /account is shared by pro/consumer
          // Parent will load the correct view (pro|consumer)
          // meta: { requiresPro: true },
          meta: {
            headerOptions: {
              light: false,
            },
          },
        },
        {
          name: 'account::pro::points',
          path: 'points',
          component: Points,
          meta: { requiresPro: true },
          redirect: {
            name: 'dashboard::pro::performance',
            params: {
              activePerformanceTab: 'points',
            },
          },
        },
        {
          name: 'account::pro::reviews',
          path: 'reviews',
          component: Reviews,
          meta: { requiresPro: true },
        },

        {
          name: 'account::pro::occupation',
          path: 'occupation/edit/:serviceProviderOccupationId?',
          component: Occupation,
          props: true,
          meta: { requiresPro: true },
        },
        {
          name: 'account::pro::tools',
          path: 'tools-offers',
          component: ToolsAndOffers,
          meta: { requiresPro: true },
        },
        {
          name: 'account::pro::invite',
          path: 'invite-a-pro',
          component: InviteAPro,
          meta: { requiresPro: true },
        },
        {
          name: 'account::pro::education',
          path: 'education',
          component: Education,
          meta: { requiresPro: true },
          redirect: {
            name: 'dashboard::pro::education',
          },
        },
      ],
    },
    {
      name: 'consumer',
      path: '/consumer',
      component: {
        render: h => h('router-view'),
      },
      meta: {
        requiresAuth: true,
        layout: 'AppLayout',
      },
      children: [
        {
          name: 'consumer::dashboard',
          path: 'dashboard',
          alias: '',
          component: ConsumerDashboard,
          meta: {
            headerOptions: {
              light: true,
            },
          },
          children: [
            {
              name: 'consumer::triage',
              path: 'job/:jobId',
              component: ConsumerTriage,
              props: true,
            },
          ],
        },
        {
          name: 'consumer::inbox',
          path: 'inbox/:jobId?/:proUserId?',
          component: ConsumerInbox,
          props: true,
          meta: {
            headerOptions: {
              light: true,
            },
          },
        },
        {
          name: 'consumer::messages',
          path: 'messages/:jobId/:skillsProfileId',
          component: ConsumerMessages,
          props: route => ({
            jobId: route.params.jobId,
            skillsProfileId: Number(route.params.skillsProfileId),
          }),
          meta: {
            layout: 'DashboardLayout',
          },
        },
      ],
    },
    {
      path: '/sms',
      component: ConsumerDashboard,
      props: route => ({ redirect: route.query.redirect }),
      meta: {
        layout: 'AppLayout',
        requiresAuth: true,
        headerOptions: {
          light: true,
        },
      },
    },
    {
      name: 'consumer::opt-in',
      path: '/consumer/opt-in',
      component: TextOptIn,
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          light: true,
        },
      },
    },
    {
      name: 'consumer::opt-in::success',
      path: '/consumer/opt-in/success',
      component: OptInSuccess,
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          light: true,
        },
      },
    },
    {
      name: 'consumer::opt-in::error',
      path: '/consumer/opt-in/error',
      component: OptInError,
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          light: true,
        },
      },
    },
    {
      name: 'consumer::reviews',
      path: '/consumer/reviews',
      component: NewConsumerReviews,
      children: [
        {
          name: 'consumer::reviews::new',
          path: 'new',
          component: NewReview,
        },
      ],
      meta: {
        layout: 'NewDashboardLayout',
        headerOptions: {
          light: true,
        },
      },
    },
    {
      name: 'leave-feedback',
      path: '/leave-feedback/:jobId/:skillsProfileId',
      redirect: to => ({
        name: 'consumer::reviews::new',
        query: {
          job_id: to.params.jobId,
          profile_id: to.params.skillsProfileId,
          skills_profiles: to.params.skillsProfileId,
          ...to.query,
        },
      }),
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: NewDashboard,
      redirect: {
        name: 'dashboard::pro::jobs',
        params: {
          jobsStatusAlias: 'new',
        },
      },
      children: [
        {
          path: ':jobsStatusAlias(new|potential|hired|completed|search|past)/:jobId?',
          name: 'dashboard::pro::jobs',
          components: {
            sidebar: DashboardSidebar,
            content: DashboardContent,
          },
        },
        {
          path: ':activePerformanceTab(summary|history|reviews|points)',
          name: 'dashboard::pro::performance',
          alias: 'account/points',
          components: {
            content: Performance,
          },
        },
        {
          path: 'agenda',
          name: 'dashboard::pro::agenda',
          components: {
            content: Agenda,
          },
        },
        {
          path: 'messages/:jobId?',
          name: 'dashboard::pro::messages',
          components: {
            content: Messages,
          },
          props: route => ({
            route,
          }),
        },
        {
          path: ':activeNotificationTab(updates|notification)',
          name: 'dashboard::pro::notifications',
          beforeEnter (to, from, next) {
            store.dispatch('prohub/whatsNew/storeTimeUpdateSeen');
            next();
          },
          components: {
            content: Notifications,
          },
        },
        {
          path: 'education',
          name: 'dashboard::pro::education',
          components: { content: Education },
          meta: { requiresPro: true },

        },
        {
          path: 'video/:videoId',
          name: 'dashboard::pro::video',
          components: { content: Video },
          meta: { requiresPro: true },
          props: route => ({
            videoId: route.params.videoId,
          }),
        },
        {
          path: 'quiz',
          name: 'dashboard::pro::quiz',
          components: { content: EducationQuiz },
          meta: { requiresPro: true },
        },
        {
          path: '*',
          redirect: {
            name: 'dashboard::pro::jobs',
            params: {
              jobsStatusAlias: 'new',
            },
          },
        },
      ],
      meta: {
        requiresAuth: true,
        requiresPro: true,
        layout: 'NewDashboardLayout',
        headerOptions: {
          showSearch: true,
        },
      },
    },
    {
      name: 'legal',
      path: '/legal/:userType',
      component: ServiceTerms,
    },
    {
      name: 'about',
      path: '/about',

      // TODO(adam): figure out why the redirect logic is inside the component
      // redirect: { name: 'about::us' },

      meta: {
        layout: 'AppLayout',
      },
      component: About,
      children: [
        {
          name: 'about::us',
          path: 'us',
          component: AboutUs,
        },
        {
          name: 'about::faq',
          path: 'faq',
          component: FrequentQuestions,
        },
        {
          name: 'about::contact',
          path: 'contact',
          component: ContactUs,
        },
        {
          name: 'about::legal',
          path: 'legal',
          component: { render: h => h('router-view') },
          redirect: { name: 'about::legal::tos' },
          children: [
            {
              path: 'tos',
              name: 'about::legal::tos',
              component: { render: h => h('router-view') },
              redirect: {
                name: 'about::legal::tos::type',
              },
              children: [
                {
                  name: 'about::legal::tos::type',
                  path: ':userType',
                  component: ServiceTerms,
                  beforeEnter: (to, from, next) => {
                    if (!to.params.userType) {
                      next({
                        name: 'about::legal::tos::type',
                        params: {
                          userType: store.getters['user/isPro'] ? 'pro' : 'consumer',
                        },
                      });
                    } else if (to.params.userType === 'pro' || to.params.userType === 'consumer') {
                      next();
                    } else {
                      next({ name: 'not-found' });
                    }
                  },
                },
              ],
            },
            {
              name: 'about::legal::privacy',
              path: 'privacy',
              component: PrivacyPolicy,
            },
          ],
        },
      ],
    },
    {
      name: 'redirect',
      path: '/redirect',
      meta: {
        layout: 'AppLayout',
      },
      props: route => route.query,
      component: Redirect,
    },
    {
      name: 'auth-token',
      path: '/auth-token',
      meta: {
        layout: 'ErrorLayout',
      },
      props: route => route.query,
      component: AuthWithToken,
    },
    {
      name: 'logout',
      path: '/logout',
      component: Logout,
    },
    {
      name: 'jobrequest::confirmation',
      path: '/request/next-steps/:leadId',
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          light: true,
        },
      },
      component: JobRequestConfirmation,
    },
    {
      name: 'jobrequest::confirm-tos',
      path: '/request/confirm-tos/:zipcode/:serviceId',
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          light: true,
          tos: true,
        },
      },
      component: TosConfirmation,
    },
    {
      name: 'job-request',
      path: '/request/:zipcode/:serviceId',
      redirect: {
        name: 'jobrequest::details',
      },
      meta: {
        layout: 'JobRequestLayout',
      },
      component: JobRequest,
      children: [
        {
          name: 'jobrequest::details',
          path: 'details',
          component: JobRequestDetails,
        },
        {
          name: 'jobrequest::matches',
          path: 'matches',
          component: JobRequestMatches,
          beforeEnter (to, from, next) {
            // TODO(volkan): this doesn't work in SSR because setup logic was moved to 'mounted'
            // TODO(adam): when this is fixed need to fix the views/JobRequest/JobRequestView too
            if (store.getters['jobrequest/details/isValid']) {
              next();
            } else {
              next({
                name: 'jobrequest::details',
                params: to.params,
                query: to.query,
              });
            }
          },
        },
        {
          name: 'jobrequest::overview',
          path: 'overview',
          component: JobRequestOverview,
          beforeEnter (to, from, next) {
            // TODO(volkan): this doesn't work in SSR because setup logic was moved to 'mounted'
            // TODO(adam): when this is fixed need to fix the views/JobRequest/JobRequestView too
            if (store.getters['jobrequest/matches/isValid']) {
              next();
            } else {
              next({
                name: 'jobrequest::matches',
                params: to.params,
                query: to.query,
              });
            }
          },
        },
      ],
    },
    {
      name: 'homepage',
      path: '',
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          transparent: false,
          light: true,

        },
      },
      component: Homepage,
      async beforeEnter (to, from, next) {
        if (store.getters['user/isPro']) {
          next({
            name: 'dashboard::pro::jobs',
            params: {
              jobsStatusAlias: 'new',
            },
          });
          return;
        }
        const authData = store.getters['skeletonKey/authData'];
        const scopes = (authData && authData.scope) || [];
        const grantType = (authData && authData.grant_type) || '';

        if (grantType === 'password_user' && !scopes.length) {
          next({
            name: 'logout',
          });
          return;
        }
        next();
      },
    },
    {
      name: 'pro-homepage',
      path: '/pro-homepage',
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          transparent: false,
          light: false,

        },
      },
      component: ProHomepage,
      beforeEnter (to, from, next) {
        if (store.getters['user/isPro']) {
          next({
            name: 'dashboard::pro::jobs',
            params: {
              jobsStatusAlias: 'new',
            },
          });
          return;
        }
        next();
      },
    },
    {
      name: 'pro-help',
      path: '/pro-help',
      beforeEnter () {
        window.location.replace('https://thdserviceprovider.force.com/pro/becomeapro?source=businesscard');
      },
    },
    {
      name: 'error',
      path: '/error',
      meta: {
        layout: 'ErrorLayout',
        headerOptions: {
          transparent: false,
          light: false,
        },
      },
      redirect: {
        name: 'error::500',
      },
      component: ErrorPage,
      children: [
        {
          name: 'error::401',
          path: '401',
          component: GenericError,
          props: { error: 401 },
        },
        {
          name: 'error::404',
          path: '404',
          component: GenericError,
          props: { error: 404 },
        },
        {
          name: 'error::500',
          path: '500',
          component: GenericError,
          props: { error: 500 },
        },
      ],
    },
    {
      name: 'permission-exception',
      path: '/permission-exception',
      meta: {
        layout: 'AppLayout',
      },
      component: PermissionException,
      props: route => route.query,
    },
    {
      name: 'login',
      path: '/login',
      meta: {
        layout: 'AppLayout',
      },
      component: Login,
      props: true,
    },
    {
      name: 'login_as',
      path: '/login-as',
      meta: {
        layout: 'AppLayout',
      },
      component: LoginAs,
    },
    {
      name: 'one-time-code',
      path: '/one-time-code',
      meta: {
        layout: 'AppLayout',
      },
      props: route => route.query,
      component: OneTimeCode,
    },
    {
      name: 'one-time-code-sign-in',
      path: '/one-time-code-sign-in',
      meta: {
        layout: 'AppLayout',
      },
      props: route => route.query,
      component: OneTimeCodeSignIn,
    },
    {
      name: 'create-a-password',
      path: '/create-a-password',
      meta: {
        layout: 'AppLayout',
      },
      props: route => route.query,
      component: CreateAPassword,
    },
    {
      name: 'forgot-password',
      path: '/forgot-password',
      meta: {
        layout: 'AppLayout',
      },
      props: route => route.query,
      component: PasswordForgot,
    },
    {
      // TODO(hamzeh) - remove this redirect once email template updated
      path: '/account/password/reset/confirm/:uid([^-]*)-:token([^/]+)',
      redirect: { name: 'reset-password' },
    },
    {
      name: 'reset-password',
      path: '/reset-password/:uid([^-]*)-:token([^/]+)',
      meta: {
        layout: 'AppLayout',
      },
      component: PasswordReset,
      props: true,
    },
    {
      name: 'welcome-password',
      path: '/welcome-password/:uid([^-]*)-:token([^/]+)',
      meta: {
        layout: 'AppLayout',
      },
      component: WelcomePassword,
      props: true,
    },
    {
      name: 'tos-password',
      path: '/tos-password',
      meta: {
        layout: 'AppLayout',
        headerOptions: {
          light: true,
          tos: true,
        },
      },
      props: route => route.query,
      component: TosPassword,
      beforeEnter (to, from, next) {
        if (!store.getters['user/isLoggedIn']) {
          next({
            name: 'homepage',
          });
          return;
        }
        next();
      },
    },
    {
      name: 'auth-redirect',
      path: '/auth-redirect',
      meta: {},
      component: AuthRedirect,
    },
    {
      name: 'not-found',
      path: '*',
      redirect: {
        name: 'error::404',
      },
    },
  ];

  const router = new Router({
    mode: 'history',
    scrollBehavior: (to) => {
      if (to.hash) {
        return {
          selector: to.hash,
          behavior: 'smooth',
        };
      }
      return { y: 0 };
    },
    routes,
  });

  router.beforeEach(async (to, from, next) => {
    const routeNames = [
      'dashboard::pro::jobs',
      'dashboard::pro::performance',
      'dashboard::pro::agenda',
      'dashboard::pro::messages',
    ];

    if (routeNames.indexOf(to.name) === -1) {
      next();
      return;
    }

    if (!store.getters['account/pro/serviceProvidersIds'].length) {
      await store.dispatch('account/pro/fetch', null, { root: true });
    }
    const [serviceProviderId] = store.getters['account/pro/serviceProvidersIds'];

    if (!Object.keys(store.state.warnings.all).length) {
      await store.dispatch('warnings/fetchWarnings', serviceProviderId, { root: true });
    }

    const warnings = store.state.warnings.all;
    const [pCode] = Object.keys(warnings).filter(code => code.startsWith('PERM-'));

    if (pCode && to.name !== 'permission-exception') {
      const warning = warnings[pCode];
      if (warning.extra_info.redirect && warning.extra_info.url) {
        const { isSpoofed } = store.state.loginspoof;
        // eslint-disable-next-line
        if (isSpoofed && confirm('PERM error received, redirect from app?') === true) {
          window.location.href = warning.extra_info.url;
        } else if (!isSpoofed) {
          window.location.href = warning.extra_info.url;
        }
      } else {
        const query = {
          message: warning.message,
          cta: warning.extra_info ? warning.extra_info.full_cta : null,
          url: warning.extra_info ? warning.extra_info.url : null,
          header: warning.extra_info ? warning.extra_info.header : null,
          code: warning.code,
        };
        next({
          name: 'permission-exception',
          query,
        });
      }
    } else {
      next();
    }
  });


  const unsubscribe = router.beforeEach(async (to, from, next) => {
    unsubscribe();
    await store.dispatch('user/status');
    next();
  });

  router.afterEach(createPageLoadEvent(events));

  router.beforeEach(createCheckUserStatus(store, router));

  // There errors are already propagated to server
  // there is no reason to print them here
  router.onError((err) => {
    // eslint-disable-next-line no-console
    console.error(err);

    let is403AndLimitedOrNoScope = false;
    if (err.response) {
      is403AndLimitedOrNoScope = err.response.status === 403;
      if (is403AndLimitedOrNoScope && store.getters['skeletonKey/authData']) {
        const limitedScope = 'users:me:read,proreferral:job:reviews:write,proreferral:job:reviews:read';
        const { scope } = store.getters['skeletonKey/authData'];
        is403AndLimitedOrNoScope = (scope === limitedScope || scope === undefined);
      }
    }

    if (!is403AndLimitedOrNoScope) {
      router.push({
        name: 'error::404',
        params: {
          0: err.url || router.currentRoute.path,
          justMessage: err.message,
        },
      });
    } else {
      store.dispatch('user/logoutAndClearUserSession');

      router.push({
        name: 'login',
        path: '/login',
        meta: {
          layout: 'AppLayout',
        },
        component: Login,
        params: { showRequestSearchPage: true },
      }).catch(() => {});
    }
  });

  return router;
}
